/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import {
    array, object, oneOf, string,
} from 'prop-types';

import { getFeatureFlag } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getBrandName } from '../../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../../../gql';
import useGetAccessTokenSafely from '../../../../../gql/hooks/useGetAccessTokenSafely';
import findContentByUID from '../../../../../gql/queries/findContentByUID';
import findRecentlyViewedProducts from '../../../../../gql/queries/findRecentlyViewedProducts';
import findTrendingProductsQuery from '../../../../../gql/queries/findTrendingProducts';
import useExperience from '../../../../../helpers/Personalization/useExperience';
import useBrowserUUID from '../../../../../helpers/useBrowserUUID';
import PLARecsBuilder from './PLARecsBuilder';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

const RECOMMENDATION_TYPES = {
    RECENTLY_VIEWED: 'recentlyViewed',
    TRENDING: 'trending',
    CUSTOMERS_ALSO_VIEWED: 'customersAlsoViewed',
};

function buildProductCategoryHierarchy(productSkus = []) {
    const formattedCategoryList = [];
    productSkus.reduce((category, product) => {
        const categoryHierarchies = product?.categoryHierarchies || [];
        // Should handle in future if pim removes master from categoryHierarchies
        const selectedHierarchy = categoryHierarchies.find((categories) => categories?.categoryHierarchyTreeList?.some((node) => node?.code === 'master'))
            || categoryHierarchies[0];

        if (selectedHierarchy?.categoryHierarchyTreeList?.length > 0) {
            const formattedHierarchy = [];
            selectedHierarchy.categoryHierarchyTreeList.forEach((categoryHierarchy) => {
                if (categoryHierarchy?.code !== 'master' && !category.includes(categoryHierarchy.label)) {
                    category.push(categoryHierarchy.label);
                    formattedHierarchy.push(categoryHierarchy.label);
                }
            });

            if (formattedHierarchy.length) formattedCategoryList.push(formattedHierarchy.join(' > '));
        }

        return category;
    }, []);
    return formattedCategoryList.filter((item, index, originalArray) => originalArray.indexOf(item) === index); // removed duplicates from occasion array.
}

function getTrendingConfig({
    brandName, browserUUID, isSympathySku, product, skuData,
}) {
    const productCategoryHierarchy = buildProductCategoryHierarchy(skuData);
    const filters = {
        primaryBrands: [brandName || '1800flowers'],
        excludeSympathy: !isSympathySku,
        excludeWine: true,
        excludeProductIds: [product.partNumber],
    };

    let title = 'Trending Products';

    if (productCategoryHierarchy?.length > 0) {
        filters.productCategoryHierarchy = productCategoryHierarchy[0];
        title = `More Products in ${productCategoryHierarchy[0]?.split(' > ')?.pop()?.replace(' - OLD', '')}`;
    }

    return {
        query: findTrendingProductsQuery(),
        variables: {
            brand: brandName || '1800flowers',
            environment: GRAPHQL_ENV,
            customerType: 'NONE',
            locationType: 'ALL',
            dateRange: 7,
            pageSize: 12,
            filters,
            browserUUID: browserUUID || '',
        },
        title,
    };
}

function getRecentlyViewedConfig({
    brandName, browserUUID, isPersonalizationHoldoutEnabled,
}) {
    return {
        query: findRecentlyViewedProducts(),
        variables: {
            brand: brandName,
            environment: GRAPHQL_ENV,
            isHoldoutEligible: isPersonalizationHoldoutEnabled,
            user: {
                anonymousId: browserUUID,
            },
        },
        title: 'Recently Viewed',
    };
}

function getCustomerAlsoViewedConfig({
    brandName, isSympathySku, product,
}) {
    return {
        queryName: 'CustomerAlsoBought',
        type: 'product',
        interactionName: `${brandName || '1800flowers'}-GetCampaign-CustomersAlsoBought`,
        userAttributes: [
            { name: 'isSympathy', value: isSympathySku ? 'true' : 'false' },
            { name: 'referenceProductId', value: product?.partNumber },
            { name: 'productName', value: product?.name },
        ],
        targeting: {
            pageType: 'product',
        },
        filterByZipCode: true,
        resolveIdentity: true,
        title: 'Customers Also Viewed',
    };
}

function getRecommendationConfig({
    brandName, browserUUID, isPersonalizationHoldoutEnabled, isSympathySku, product, recommendationType, skuData,
}) {
    switch (recommendationType) {
        case RECOMMENDATION_TYPES.TRENDING:
            return getTrendingConfig({
                brandName, browserUUID, isSympathySku, product, skuData,
            });
        case RECOMMENDATION_TYPES.CUSTOMERS_ALSO_VIEWED:
            return getCustomerAlsoViewedConfig({
                brandName, isSympathySku, product,
            });
        case RECOMMENDATION_TYPES.RECENTLY_VIEWED:
        default:
            return getRecentlyViewedConfig({
                brandName, browserUUID, isPersonalizationHoldoutEnabled,
            });
    }
}

function getProductsByRecommendationType({ data, product, recommendationType }) {
    if (recommendationType === RECOMMENDATION_TYPES.TRENDING) {
        return data?.trendingProducts?.topProducts?.length > 0
            ? data.trendingProducts.topProducts.map((listItem) => listItem.product)
            : [];
    }

    if (recommendationType === RECOMMENDATION_TYPES.CUSTOMERS_ALSO_VIEWED) {
        return data?.experience?.products?.length > 0 ? data.experience.products : [];
    }

    const recentlyViewedProducts = data?.recentlyViewed?.products?.length > 0 ? data.recentlyViewed.products : [];
    return recentlyViewedProducts.filter((elem) => elem.partNumber !== product.partNumber);
}

function getTitleByRecommendationType({ data, recommendationType, recommendationConfig }) {
    if (recommendationType === RECOMMENDATION_TYPES.CUSTOMERS_ALSO_VIEWED) {
        return data?.experience?.experiment?.value?.productRecRecipe?.title || recommendationConfig.title;
    }
    return recommendationConfig.title;
}

export default function PLAPersonalizedRecsContainer({
    product, skuData, cmsRecBlockData, recommendationType, dataTestId,
}) {
    const browserUUID = useBrowserUUID();
    const brandName = useSelector(getBrandName);
    const jwt = useGetAccessTokenSafely();
    const isPersonalizationHoldoutEnabled = useSelector(getFeatureFlag('is-personalization-holdout-enabled'));
    const isSympathySku = skuData?.some((sku) => sku?.sympathyValue === '1' || sku?.sympathyValue === '2');

    if (!cmsRecBlockData?.reference?.[0]?.uid) {
        return null;
    }

    const findContentOptions = {
        brand: brandName || '1800flowers',
        contentType: cmsRecBlockData?.reference?.[0]?._content_type_uid,
        uid: cmsRecBlockData?.reference?.[0]?.uid,
        includeRefs: ['personalized_product_card_reference'],
    };

    const CONTENT_DETAILS = findContentByUID(findContentOptions);
    const { data: contentData, loading: contentLoading, error: contentError } = useQuery(CONTENT_DETAILS);

    const recommendationConfig = getRecommendationConfig({
        brandName,
        browserUUID,
        isPersonalizationHoldoutEnabled,
        isSympathySku,
        product,
        recommendationType,
        skuData,
    });

    const personalizedRecQuery = recommendationConfig.query ? useQuery(recommendationConfig.query, {
        variables: recommendationConfig.variables,
        context: {
            api: 'personalizedContent',
            jwt,
        },
        skip: !jwt || !browserUUID,
        fetchPolicy: 'network-only',
    }) : useExperience({
        ...recommendationConfig,
        featureFlag: recommendationType === RECOMMENDATION_TYPES.CUSTOMERS_ALSO_VIEWED,
    });

    const {
        loading, error, data, variables,
    } = personalizedRecQuery;
    const experimentTracking = data?.experience?.experimentTracking;

    useClickStreamCustomEventsTracking({
        experimentTracking,
        isFireImpression: true,
        page: { type: 'product' },
    });

    if (!jwt || !browserUUID) {
        return null;
    }

    if (loading || variables?.skip || contentLoading) {
        return null;
    }

    if (error || contentError) {
        mbpLogger.logError({
            function: 'PLAPersonalizedRecsContainer.js',
            message: `Error loading data for ${recommendationType} products`,
            appName: process.env.npm_package_name,
            module: 'PLAPersonalizedRecsContainer',
            error: error || contentError,
        });
        return null;
    }

    const products = getProductsByRecommendationType({ data, product, recommendationType });
    const title = getTitleByRecommendationType({ data, recommendationType, recommendationConfig });
    const contentEntry = contentData?.findContentByUID?.content?.entry;

    if (products.length > 0 && contentEntry?.uid) {
        return (
            <PLARecsBuilder
                data={data}
                products={products}
                contentData={contentEntry}
                title={title}
                trackGA4ProductClick
                dataTestId={dataTestId}
                experimentTracking={experimentTracking}
            />
        );
    }

    return null;
}

PLAPersonalizedRecsContainer.recommendationTypes = RECOMMENDATION_TYPES;

PLAPersonalizedRecsContainer.propTypes = {
    product: object.isRequired,
    skuData: array.isRequired,
    cmsRecBlockData: object,
    recommendationType: oneOf(Object.values(RECOMMENDATION_TYPES)).isRequired,
    dataTestId: string,
};

PLAPersonalizedRecsContainer.defaultProps = {
    cmsRecBlockData: {},
    dataTestId: '',
};
